/**
 * Create Loan Page
 * Form for creating new loan applications
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { useDispatch } from 'react-redux';

import Button from '../../../components/common/Button/Button';
import { createLoan } from '../loanSlice';
import { ROUTES } from '../../../constants/routes';
import { loanSchema } from '../validations/loanValidation';

const CreateLoan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loanSchema),
  });

  const amount = watch('amount', 0);
  const interestRate = watch('interestRate', 12);
  const termMonths = watch('termMonths', 12);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');
    try {
      await dispatch(createLoan(data)).unwrap();
      navigate(ROUTES.LOANS.LIST);
    } catch (err) {
      setError(err.message || 'Failed to create loan');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate(ROUTES.LOANS.LIST)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Apply for Loan</h1>
          <p className="text-gray-600 dark:text-gray-400">Submit a new loan application</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Member ID</label>
            <input {...register('memberId')} type="text" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
            {errors.memberId && <p className="mt-1 text-sm text-red-600">{errors.memberId.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Loan Type</label>
            <select {...register('loanType')} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
              <option value="">Select Type</option>
              <option value="personal">Personal Loan</option>
              <option value="business">Business Loan</option>
              <option value="emergency">Emergency Loan</option>
              <option value="education">Education Loan</option>
            </select>
            {errors.loanType && <p className="mt-1 text-sm text-red-600">{errors.loanType.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (TSh)</label>
            <input {...register('amount')} type="number" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Interest Rate (%)</label>
            <input {...register('interestRate')} type="number" step="0.1" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
            {errors.interestRate && <p className="mt-1 text-sm text-red-600">{errors.interestRate.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Term (Months)</label>
            <input {...register('termMonths')} type="number" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
            {errors.termMonths && <p className="mt-1 text-sm text-red-600">{errors.termMonths.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purpose</label>
            <input {...register('purpose')} type="text" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-2">Loan Summary</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Estimated calculations will appear here...</p>
        </div>

        <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={() => navigate(ROUTES.LOANS.LIST)}>Cancel</Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>Submit Application</Button>
        </div>
      </form>
    </div>
  );
};

export default CreateLoan;
